'use client';

import { useCallback, useState, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import ProductListing from '@/app/_components/ProductListing';
const SimilarProductsCarousel = ({
  products = [],
  hideDetails = false,
  onLoadMore
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    slidesToScroll: 2,
    containScroll: 'trimSnaps'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const updateScrollButtons = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);
  useEffect(() => {
    if (!emblaApi) return;
    updateScrollButtons();
    emblaApi.on('select', updateScrollButtons);
    emblaApi.on('reInit', updateScrollButtons);
    return () => {
      emblaApi.off('select', updateScrollButtons);
      emblaApi.off('reInit', updateScrollButtons);
    };
  }, [emblaApi, updateScrollButtons]);
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);
  const pendingScrollRef = useRef(false);
  const scrollNext = useCallback(async () => {
    if (!emblaApi) return;

    // Check if we're near the end and should load more
    const currentIndex = emblaApi.selectedScrollSnap();
    const totalSnaps = emblaApi.scrollSnapList().length;

    // Load more when we're in the last few items
    if (onLoadMore && !isLoading && currentIndex >= totalSnaps - 3) {
      setIsLoading(true);
      pendingScrollRef.current = true;
      try {
        await onLoadMore();
      } finally {
        setIsLoading(false);
      }
    } else {
      emblaApi.scrollNext();
    }
  }, [emblaApi, onLoadMore, isLoading]);

  // Reinitialize embla when products change, and scroll if pending
  useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit();
      if (pendingScrollRef.current) {
        emblaApi.scrollNext();
        pendingScrollRef.current = false;
      }
    }
  }, [emblaApi, products.length]);
  if (!products || products.length === 0) return null;
  const anyOnSale = products.some(p => p.on_sale == 1);
  const saleSpacerClasses = 'row-has-sale-base row-has-sale-md row-has-sale-xl row-has-sale-2xl';
  return <div className="site-padding mb-16 w-full min-[1810px]:px-0 relative">
            <div className="mb-8">
                <h2 className="leading-[1] text-2xl font-extralight">Similar Products</h2>
                <p className="text-xs text-light-3 m-0">You may also like these products</p>
            </div>

            <div className="flex items-center -mr-3">
                {/* Left Arrow */}
                <button onClick={scrollPrev} disabled={!canScrollPrev} aria-label="Previous products" className={`absolute left-[calc(var(--site-padding)+0.625rem)] min-[1810px]:left-[0.625rem] top-[40%] z-40 shrink-0 ${canScrollPrev ? 'cursor-pointer' : 'cursor-default opacity-30'}`}>
                    <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:shadow-lg transition-shadow">
                        <FontAwesomeIcon icon={faChevronLeft} className="text-dark text-sm" />
                    </div>
                </button>

                {/* Carousel */}
                <div className="flex-1 min-w-0">
                    <div ref={emblaRef} className="overflow-hidden">
                        <div className="flex">
                            {products.map((product, index) => <div key={`${product.sub_product_id}-${index}`} className="flex-none w-1/2 sm:w-1/3 md:w-1/4 xl:w-1/5 2xl:w-[16.666%] pr-3">
                                    <ProductListing product={product} hideDetails={hideDetails} className={anyOnSale && product.on_sale != 1 ? saleSpacerClasses : ''} />
                                </div>)}
                        </div>
                    </div>
                </div>

                {/* Right Arrow */}
                <button onClick={scrollNext} disabled={!canScrollNext && !onLoadMore} aria-label="Next products" className={`absolute right-[calc(var(--site-padding)+0.625rem)] min-[1810px]:right-[0.625rem] top-[40%] z-40 shrink-0 ${canScrollNext || onLoadMore ? 'cursor-pointer' : 'cursor-default opacity-30'}`}>
                    <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:shadow-lg transition-shadow">
                        <FontAwesomeIcon icon={faChevronRight} className="text-dark text-sm" />
                    </div>
                </button>
            </div>
        </div>;
};
export default SimilarProductsCarousel;